import React, { useMemo, useState } from "react";
import { LineChart as LineIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ForecastChart, LOAD_OVER, LOAD_WARN } from "@/components/ForecastChart";

const sum = (series, key) => Math.round(series.reduce((s, r) => s + (r[key] || 0), 0) * 10) / 10;

const peakPct = (series) =>
  series.reduce((m, r) => (r.quota_m3 > 0 ? Math.max(m, Math.round((r.projected_m3 / r.quota_m3) * 100)) : m), 0);

const MuniBreakdownTable = ({ munis, t, onPick }) => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse text-sm" data-testid="util-forecast-breakdown">
      <thead>
        <tr className="border-b border-border label-caps text-left">
          <th className="py-2 pr-3">{t("municipality")}</th>
          <th className="py-2 pr-3">{t("forecast_booked_pool")}</th>
          <th className="py-2 pr-3">{t("forecast_booked_large")}</th>
          <th className="py-2 pr-3">{t("projected")}</th>
          <th className="py-2 pr-3">{t("forecast_peak")}</th>
        </tr>
      </thead>
      <tbody>
        {munis.map((m) => {
          const peak = peakPct(m.series);
          return (
            <tr
              key={m.id}
              className="cursor-pointer border-b border-border/60 hover:bg-muted/40"
              onClick={() => onPick(m.id)}
              data-testid={`util-forecast-row-${m.id}`}
            >
              <td className="py-2 pr-3 font-medium">{m.name}</td>
              <td className="py-2 pr-3 font-mono-jb">{sum(m.series, "booked_pool_m3")} m³</td>
              <td className="py-2 pr-3 font-mono-jb">{sum(m.series, "booked_large_m3")} m³</td>
              <td className="py-2 pr-3 font-mono-jb">{sum(m.series, "projected_m3")} m³</td>
              <td className={`py-2 pr-3 font-mono-jb ${peak >= LOAD_OVER ? "text-destructive" : peak >= LOAD_WARN ? "text-yellow-600" : "text-primary"}`}>
                {peak}%
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export const UtilityForecastPanel = ({ forecast, t }) => {
  const [scope, setScope] = useState("all");
  const munis = forecast?.municipalities || [];
  const current = useMemo(
    () => (scope === "all" ? forecast?.series : munis.find((m) => m.id === scope)?.series) || [],
    [scope, forecast, munis],
  );

  return (
    <div className="space-y-4">
      <Card className="border-border">
        <CardContent className="p-6">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <LineIcon className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">{t("forecast")} (30d)</h3>
            </div>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-full sm:w-64" data-testid="util-forecast-scope-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="util-forecast-scope-all">{t("forecast_cumulative")}</SelectItem>
                {munis.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`util-forecast-scope-${m.id}`}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="mb-4 text-xs text-muted-foreground">{t("forecast_all_bookings_hint")}</p>
          <ForecastChart series={current} t={t} testId="util-forecast-chart" />
        </CardContent>
      </Card>
      {munis.length > 0 && (
        <Card className="border-border">
          <CardContent className="p-6">
            <h3 className="mb-4 text-lg font-semibold">{t("forecast_by_municipality")}</h3>
            <MuniBreakdownTable munis={munis} t={t} onPick={setScope} />
          </CardContent>
        </Card>
      )}
    </div>
  );
};
